import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Chip, Divider, Typography } from "@material-ui/core";
import {
  CallState,
  CorpSessionInfo,
  TokenizeCardErrors,
  TravelProductEnum,
} from "redmond";
import {
  GenericInfoPopup,
  B2BSpinner,
  LoadingIndicator,
  useDeviceTypes,
  LoadingPopup,
  PoweredByHopper,
  CheckoutPaymentForm,
  B2BPaymentMethodSelectWorkflow,
  Icon,
  IconName,
  createElementId,
  TEST_CARD_LAST_FOURS,
  NotificationBanner,
  BannerSeverity,
  getPaymentCardSubtitle,
  paymentTextConstants,
  PrefillPaymentFormData,
  Tooltip,
  usePrevious,
  SanitizedHtml,
} from "halifax";
import clsx from "clsx";
import { useExperimentIsVariant } from "@capone/experiments";
import { RouteComponentProps } from "react-router";
import { isCorpTenant, isVesperTenant, getConfigTenant } from "@capone/common";
import { shouldAutoSelectCard } from "@capone/common/src/utils/shouldAutoSelectCard";

import { RewardsSelection } from "../RewardsSelection";
import { PaymentCardConnectorProps } from "./container";
import { config } from "../../../../api/config";
import { CheckoutSteps } from "../../reducer/selectors";
import {
  ANNUAL_TRAVEL_CREDITS,
  AVAILABLE,
  C1_AUTO_SELECT_CARD_AT_CHECKOUT,
  COMBINED_REWARDS_CHECKOUT,
  CONTROL,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_SALE,
  TRAVEL_SALE_VARIANTS,
  useExperiments,
  VCN_ENABLEMENT,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { TravelWalletSelection } from "../TravelWalletSelection";
import { PayFlexSurvey } from "../PayFlexSurvey";
import "./styles.scss";
import { ClientContext } from "../../../../App";
import { FLIGHTS_REWARDS_DISABLED_BANNER_TEXT } from "../../../rewards/components/RewardsBanner/constants";

const getAddPaymentMethodSubtitle = (
  userIsPrimary: boolean,
  creditAndOfferStackingExperimentV1: boolean,
  matchesMobile: boolean,
  canRedeemRewards: boolean,
  isMultiRewardsEligible: boolean,
) => {
  if (!userIsPrimary) {
    return paymentTextConstants.PAYMENT_STEP_SUBTITLE(false);
  }

  if (creditAndOfferStackingExperimentV1) {
    return isMultiRewardsEligible
      ? paymentTextConstants.PAYMENT_CARD_SUBTITLE_WITH_CREDITS_AND_OFFERS_COMBINED_REWARDS
      : paymentTextConstants.PAYMENT_CARD_SUBTITLE_WITH_CREDITS_AND_OFFERS;
  }

  return matchesMobile
    ? paymentTextConstants.MOBILE_PAYMENT_STEP_SUBTITLE(canRedeemRewards)
    : paymentTextConstants.PAYMENT_STEP_SUBTITLE(canRedeemRewards);
};

const PaymentAddingElement = () => (
  <LoadingPopup
    indicatorSize="small"
    indicator={B2BSpinner}
    open
    popupSize="small"
    message="Adding your payment method"
    footer={PoweredByHopper}
  />
);

export interface IPaymentCardProps
  extends PaymentCardConnectorProps,
    RouteComponentProps {
  disabled?: boolean;
  // note: this prop is used to overwrite the copy in step-title
  paymentStepTitle?: string;
  className?: string;
  tripProtectionBanner?: React.ReactNode;
}

export const PaymentCard = ({
  listPaymentMethods,
  verifyPaymentMethod,
  deletePaymentMethod,
  setSelectedPaymentMethodId,
  fetchRewardsAccounts,
  fetchProductToEarn,
  fetchAllEarnForProduct,
  selectedRewardsPaymentAccount,
  selectedRewardsPaymentAccountId,
  isCreditCardPaymentRequired,
  totalCreditCardPaymentRequired,
  paymentMethods,
  rewardsAccounts,
  selectedPaymentMethodId,
  listPaymentMethodCallState,
  verifyPaymentMethodCallState,
  deletePaymentMethodCallState,
  paymentStepTitle,
  disabled = false,
  getCheckoutStepNumber,
  hasError,
  className,
  tripProtectionBanner,
  isTravelCreditPaymentOnly,
  isStackedTravelWalletPaymentOnly,
  isTravelWalletOfferPaymentOnly,
  canRedeemRewards,
  viewedTripSummaryProperties,
  canUseTravelWallet,
  offerToApply,
  earnByRewardsAccountId,
  totalCardPaymentRequired,
  userIsPrimary,
  credits,
  filteredOffers,
  walletLoaded,
  userSelectedRewardsManually,
}: IPaymentCardProps) => {
  const { matchesMobile } = useDeviceTypes();
  const [openErrorPaymentModal, setOpenErrorPaymentModal] = useState(false);
  const [isNotCapOneAccount, setIsNotCapOneAccount] = useState(false);
  const [tokenizeErrors, setTokenizeErrors] = useState<TokenizeCardErrors[]>(
    [],
  );
  const [paymentFormPrefillData, setPaymentFormPrefillData] =
    useState<PrefillPaymentFormData | null>(null);
  const [refreshCredentialsKey, setRefreshCredentialsKey] = useState<
    number | null
  >(null);

  const expState = useExperiments();
  const { sessionInfo, isRewardsDisabled } = useContext(ClientContext);

  const allowAnyCards = useExperimentIsVariant(
    "c1-corporate-credit-card-validation",
    "any-spark",
  );

  const vcnEnablement = getExperimentVariant(
    expState.experiments,
    VCN_ENABLEMENT,
  );
  const isVCNEnabled = React.useMemo(
    () => vcnEnablement === AVAILABLE,
    [vcnEnablement],
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1,
  );
  const isCreditAndOfferStackingExperimentV1 = useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1],
  );

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;
  const isCorporateTenant = isCorpTenant(config.TENANT);
  const isVesper = isVesperTenant(getConfigTenant());

  const combinedRewardsCheckoutExperiment = getExperimentVariant(
    expState.experiments,
    COMBINED_REWARDS_CHECKOUT,
  );
  const isMultiRewardsEligible = useMemo(
    () =>
      combinedRewardsCheckoutExperiment === AVAILABLE && !isCorporateTenant,
    [combinedRewardsCheckoutExperiment, isCorporateTenant],
  );

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS,
  );
  const isTravelSaleEnabled = travelSaleVariant !== CONTROL;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const corpSessionInfo = sessionInfo && (sessionInfo as CorpSessionInfo);

  const isNonFinancialUser = corpSessionInfo?.corporateInfo?.cap1Role === "NFU";

  useEffect(() => {
    listPaymentMethods();
    fetchRewardsAccounts(true, sessionInfo, true);
  }, []);

  const autoSelectExperimentOn =
    getExperimentVariant(
      expState.experiments,
      C1_AUTO_SELECT_CARD_AT_CHECKOUT,
    ) === AVAILABLE;

  useEffect(() => {
    const toSelect = shouldAutoSelectCard({
      paymentMethods,
      filteredOffers,
      credits,
      selectedPaymentMethodId,
      experimentOn: autoSelectExperimentOn,
      walletLoaded,
    });
    if (!toSelect) return;
    const card = paymentMethods[0];
    const matchingRewardsAccount = rewardsAccounts.find(
      (account) =>
        account.lastFour === card.last4 ||
        (card.last4 &&
          account.lastFourVirtualCardNumbers?.includes(card.last4)),
    );
    setSelectedPaymentMethodId({
      paymentMethodId: toSelect,
      accountId: matchingRewardsAccount?.accountReferenceId,
      multiTicketType: viewedTripSummaryProperties?.multi_ticket_type,
    });
  }, [
    paymentMethods,
    filteredOffers,
    credits,
    autoSelectExperimentOn,
    walletLoaded,
    rewardsAccounts,
    viewedTripSummaryProperties,
    setSelectedPaymentMethodId,
  ]);

  const prevVerifyPaymentMethodCallState = usePrevious(
    verifyPaymentMethodCallState,
  );

  useEffect(() => {
    if (isCorpTenant(config.TENANT)) {
      fetchAllEarnForProduct();
    }
  }, [totalCardPaymentRequired?.value, offerToApply, paymentMethods.length]);

  useEffect(() => {
    if (
      isCorporateTenant &&
      prevVerifyPaymentMethodCallState === CallState.InProcess &&
      verifyPaymentMethodCallState === CallState.Failed
    ) {
      setRefreshCredentialsKey((prev) => (prev ?? 0) + 1);
    }
  }, [
    verifyPaymentMethodCallState,
    prevVerifyPaymentMethodCallState,
    isCorporateTenant,
  ]);

  useEffect(() => {
    if (
      prevVerifyPaymentMethodCallState === CallState.InProcess &&
      verifyPaymentMethodCallState === CallState.Success
    ) {
      setPaymentFormPrefillData(null);
    }
    if (
      isCorporateTenant &&
      prevVerifyPaymentMethodCallState === CallState.Failed &&
      verifyPaymentMethodCallState !== CallState.Failed
    ) {
      setPaymentFormPrefillData(null);
    }
  }, [
    verifyPaymentMethodCallState,
    prevVerifyPaymentMethodCallState,
    isCorporateTenant,
  ]);

  const ineligibleRewardsAccounts = useMemo(
    () =>
      rewardsAccounts.filter(
        (account) => !(account.allowRewardsRedemption ?? true),
      ),
    [rewardsAccounts],
  );

  const handleOnAddPaymentMethod = (token: string, last4: string) => {
    // note: cap1 specific logic:
    // A card should be deemed ineligible if the last four digits do not match one of the cards associated with their accounts.
    const account = rewardsAccounts.find(
      (account) =>
        account.lastFour === last4 ||
        account.lastFourVirtualCardNumbers?.includes(last4) ||
        account.discoverNetworkFlipInfo?.transferFromCardNumberLastFour ===
          last4,
    );
    let matchingRewardsAccount = account;

    const isAddingVCNPaymentMethod = !!(
      matchingRewardsAccount?.lastFourVirtualCardNumbers &&
      matchingRewardsAccount?.lastFourVirtualCardNumbers?.includes(last4)
    );

    // TODO: bad practice, remove this in favor of real test accounts when we have them
    const isTestCard =
      window.__mclean_env__.ENV !== "production" &&
      TEST_CARD_LAST_FOURS.includes(last4);
    if (isTestCard) {
      matchingRewardsAccount = rewardsAccounts[0];
    }

    if (!!matchingRewardsAccount || isTestCard || isCorpTenant(config.TENANT)) {
      verifyPaymentMethod(
        { token },
        matchingRewardsAccount?.accountReferenceId!,
        isAddingVCNPaymentMethod,
      );
    } else {
      setIsNotCapOneAccount(true);
      setOpenErrorPaymentModal(true);
    }
  };

  const handleCloseErrorPopup = () => {
    isNotCapOneAccount && setIsNotCapOneAccount(false);
    setOpenErrorPaymentModal(false);
  };

  const renderCheckoutPaymentForm = (isOpen: boolean) => {
    const isLoading = verifyPaymentMethodCallState === CallState.InProcess;
    return (
      <CheckoutPaymentForm
        loading={isLoading}
        loadingEl={<PaymentAddingElement />}
        onSubmit={(token, last4, prefillData) => {
          handleOnAddPaymentMethod(token, last4);
          setPaymentFormPrefillData(prefillData);
        }}
        saveLabel="Save"
        onError={(errors) => {
          setTokenizeErrors(errors);
          setOpenErrorPaymentModal(true);
        }}
        spreedlyEnvironmentKey={config.spreedlyEnvironmentKey}
        isMobile={matchesMobile}
        className="b2b"
        buttonClassName="b2b"
        tenant={config.TENANT}
        prefillData={!isLoading ? paymentFormPrefillData : null}
        isOpen={isOpen}
        refreshCredentialsKey={isCorporateTenant ? refreshCredentialsKey : null}
      />
    );
  };

  const isPaymentMethodSelectDisabled =
    selectedRewardsPaymentAccount?.isTiered ||
    !isCreditCardPaymentRequired ||
    !totalCreditCardPaymentRequired ||
    (selectedRewardsPaymentAccountId === undefined &&
      !userSelectedRewardsManually);

  const getAddPaymentCtaText = () => {
    if (isNonFinancialUser) {
      return paymentTextConstants.CTA_CORP_NON_FINANCIAL_USER_CREDIT_CARD_TEXT;
    }

    return rewardsAccounts.length === 1 ? (
      <Box
        className={clsx("add-payment-cta-container", {
          mobile: matchesMobile,
          disabled: disabled || isPaymentMethodSelectDisabled,
        })}
      >
        <Typography variant="body1">
          {paymentTextConstants.CTA_SINGLE_ACCOUNT_ADD_YOUR_TEXT}
          <b className="card-name">{rewardsAccounts[0].productDisplayName}</b>
          {paymentTextConstants.CTA_SINGLE_ACCOUNT_CREDIT_CARD_TEXT}
        </Typography>
        {rewardsAccounts[0].earn.flightsMultiplier &&
        rewardsAccounts[0].earn.flightsMultiplier > 0 ? (
          <Chip
            label={`Earn ${rewardsAccounts[0].earn.flightsMultiplier}X on flights`}
            className={clsx("earn-chip", {
              enabled: !isPaymentMethodSelectDisabled,
            })}
          />
        ) : null}
      </Box>
    ) : (
      paymentTextConstants.ADD_PAYMENT_METHOD_CTA_MULTIPLE_ACCOUNTS
    );
  };

  const paymentMethodWorkflowTitles = () => ({
    addPaymentCta: getAddPaymentCtaText(),
    addPaymentModalTitle: paymentTextConstants.ADD_PAYMENT_METHOD_MODAL_TITLE,
    addAdditionalPaymentCta:
      paymentTextConstants.ADD_ADDITIONAL_PAYMENT_METHOD_CTA,
    backToCardSelectionCta: paymentTextConstants.BACK_TO_CARD_SELECTION_CTA,
    paymentFormHeader: (cardName: string) => (
      <SanitizedHtml
        as={Typography}
        variant="h4"
        html={paymentTextConstants.ADD_PAYMENT_FORM_HEADER_TEXT(cardName)}
      />
    ),
    cardEndingIn: paymentTextConstants.CARD_ENDING_IN_TEXT,
  });

  const getPaymentCardTitle = (): string => {
    if (paymentStepTitle) {
      return paymentStepTitle;
    }
    if (matchesMobile) {
      return paymentTextConstants.MOBILE_PAYMENT_STEP_TITLE(canRedeemRewards);
    }
    return paymentTextConstants.PAYMENT_STEP_TITLE(
      getCheckoutStepNumber(CheckoutSteps.REWARDS_AND_PAYMENT),
      canRedeemRewards,
    );
  };
  const getParadiseCardMessage = () =>
    rewardsAccounts.find((acct) =>
      acct.productDisplayName.toLowerCase().includes("paradise"),
    ) ? (
      <NotificationBanner
        className={clsx("paradise-banner-notification", {
          mobile: matchesMobile,
        })}
        label="Bookings made with your Paradise card through Capital One Travel cannot be serviced by concierge services. If you’d like concierge to manage your booking, book in the Capital One concierge app."
        severity={BannerSeverity.NOTICE}
      />
    ) : null;

  const primaryButton = {
    buttonText: paymentTextConstants.EDIT_PAYMENT_METHOD,
    onClick: () => {
      handleCloseErrorPopup();
    },
    defaultStyle: "h4r-secondary",
  };
  const secondaryButton = {
    buttonText: paymentTextConstants.TRY_AGAIN,
    onClick: () => {
      handleCloseErrorPopup();
    },
    defaultStyle: "h4r-primary",
    buttonWrapperClassName: "b2b",
  };

  const rewardsTitleId = createElementId("rewardsTitle");
  const rewardsSubtitleId = createElementId("rewardsSubtitle");

  const travelWalletSelection = (
    <TravelWalletSelection
      disabled={disabled}
      isMobile={matchesMobile}
      isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
      isCreditAndOfferStackingExperimentV1={
        isCreditAndOfferStackingExperimentV1
      }
      isTravelSale={isTravelSaleEnabled}
    />
  );
  const rewardsSection = (
    <RewardsSelection
      {...{
        disabled:
          disabled ||
          isRewardsDisabled ||
          (isCreditAndOfferStackingExperimentV1 &&
            (isTravelCreditPaymentOnly ||
              isStackedTravelWalletPaymentOnly ||
              isTravelWalletOfferPaymentOnly)),
        rewardsTitleId,
        rewardsSubtitleId,
      }}
    />
  );

  return (
    <>
      {listPaymentMethodCallState === CallState.InProcess ||
      deletePaymentMethodCallState === CallState.InProcess ? (
        <LoadingIndicator
          indicatorSize="small"
          indicator={B2BSpinner}
          message={
            listPaymentMethodCallState === CallState.InProcess
              ? `Fetching`
              : `Deleting`
          }
        />
      ) : (
        <Box
          className={clsx(
            "payment-methods-container",
            {
              mobile: matchesMobile,
              disabled,
              xmod: isXmodRedesign,
              redesigned: isVesper,
            },
            className,
          )}
        >
          <Typography
            className="step-title"
            variant={matchesMobile && !isXmodRedesign ? "h4" : "h2"}
          >
            {getPaymentCardTitle()}
          </Typography>
          <Box className="payment-step-subtitle-wrapper">
            <Typography
              variant="body2"
              className={clsx("payment-step-subtitle-text", {
                vesper: isVesper,
              })}
            >
              {getAddPaymentMethodSubtitle(
                userIsPrimary,
                isCreditAndOfferStackingExperimentV1,
                matchesMobile,
                canRedeemRewards,
                isMultiRewardsEligible,
              )}
            </Typography>
            <Tooltip
              content={
                <Typography className="payment-earn-info-popover-content">
                  {paymentTextConstants.PAYMENT_EARN_INFO_TOOLTIP_TEXT}
                </Typography>
              }
              triggerClassName="payment-earn-info-btn"
              title="Rewards & Payment"
            >
              <Icon name={IconName.InfoCircle} />
            </Tooltip>
          </Box>
          {tripProtectionBanner}
          {canRedeemRewards && ineligibleRewardsAccounts.length > 0 && (
            <NotificationBanner
              className="authorized-users-rewards-banner"
              label={paymentTextConstants.INELIGIBLE_ACCOUNTS_NOTICE(
                rewardsAccounts,
              )}
              severity={BannerSeverity.NOTICE}
              tooltip={
                rewardsAccounts.length === ineligibleRewardsAccounts.length
                  ? {
                      label: paymentTextConstants.INELIGIBLE_ACCOUNTS_TOOLTIP(
                        ineligibleRewardsAccounts,
                      ),
                      icon: IconName.InfoCircle,
                    }
                  : undefined
              }
            />
          )}

          {(canRedeemRewards || canUseTravelWallet) && (
            <Divider className="payment-methods-container-divider" />
          )}
          {isCreditAndOfferStackingExperimentV1 &&
            canUseTravelWallet &&
            travelWalletSelection}
          {getParadiseCardMessage()}
          {canRedeemRewards && (
            <>
              <Typography
                variant="h4"
                component="h3"
                className="rewards-account-title"
                id={rewardsTitleId}
              >
                {paymentTextConstants.REWARDS_ACCOUNT_TITLE(
                  config.TENANT,
                  isMultiRewardsEligible,
                )}
              </Typography>
              <SanitizedHtml
                as={Typography}
                variant="body2"
                html={paymentTextConstants.REWARDS_ACCOUNT_SUBTITLE(
                  config.TENANT,
                  isMultiRewardsEligible,
                )}
                id={rewardsSubtitleId}
                className="rewards-accounts-subtitle"
              />
              {isRewardsDisabled && (
                <NotificationBanner
                  className="rewards-disabled-banner"
                  label={FLIGHTS_REWARDS_DISABLED_BANNER_TEXT}
                  severity={BannerSeverity.WARNING}
                  icon={
                    <Icon
                      className="banner-icon"
                      name={IconName.WarningAlert}
                    />
                  }
                />
              )}
              {rewardsSection}
            </>
          )}

          <PayFlexSurvey />

          <Divider className="payment-methods-container-divider" />

          <Typography
            className="payment-method-title"
            variant="h4"
            component="h3"
          >
            {paymentTextConstants.PAYMENT_METHOD_TITLE}
          </Typography>

          {getPaymentCardSubtitle(
            rewardsAccounts[0],
            corpSessionInfo?.corporateInfo?.businessName ?? "",
            isNonFinancialUser,
            config.TENANT,
            totalCreditCardPaymentRequired,
            !selectedPaymentMethodId,
            isPaymentMethodSelectDisabled,
          )}

          <B2BPaymentMethodSelectWorkflow
            errorModalOpen={openErrorPaymentModal}
            rewardsAccounts={rewardsAccounts ?? []}
            savedPayments={paymentMethods ?? []}
            selectedPaymentHopperId={selectedPaymentMethodId}
            disabled={
              isPaymentMethodSelectDisabled ||
              disabled ||
              hasError ||
              (isCreditAndOfferStackingExperimentV1 &&
                (isTravelCreditPaymentOnly ||
                  isStackedTravelWalletPaymentOnly ||
                  isTravelWalletOfferPaymentOnly))
            }
            selectPaymentMethod={(paymentId, rewardsAccount) => {
              setSelectedPaymentMethodId({
                paymentMethodId: paymentId,
                accountId: rewardsAccount?.accountReferenceId,
                multiTicketType: viewedTripSummaryProperties?.multi_ticket_type,
              });
              fetchProductToEarn();
            }}
            removePaymentMethod={(paymentId: string) => {
              deletePaymentMethod({ paymentId });
            }}
            titles={paymentMethodWorkflowTitles()}
            renderCheckoutPaymentForm={renderCheckoutPaymentForm}
            isMobile={matchesMobile}
            product={TravelProductEnum.Flights}
            loading={verifyPaymentMethodCallState === CallState.InProcess}
            buttonClassName="b2b"
            fullScreenWithBanner={matchesMobile}
            paymentMethodDisabled={(_r) => false}
            isVCNEnabled={isVCNEnabled}
            allowAnyCards={allowAnyCards || isNonFinancialUser}
            tenant={config.TENANT}
            corporateInfo={corpSessionInfo?.corporateInfo}
            showEarnValue={Object.values(earnByRewardsAccountId).some(
              (earn) => earn.amount > 0,
            )}
            earnValuesByRewardAcctId={earnByRewardsAccountId}
          />
        </Box>
      )}
      <GenericInfoPopup
        open={openErrorPaymentModal}
        image={
          <Icon
            className="error-icon"
            name={
              isNotCapOneAccount
                ? IconName.ErrorState
                : IconName.UnableToProcess
            }
          />
        }
        title={
          isNotCapOneAccount
            ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_TITLE
            : paymentTextConstants.UNABLED_TO_ADD_PAYMENT
        }
        subtitle={
          isNotCapOneAccount
            ? paymentTextConstants.CAP_ONE_INVALID_CREDIT_CARD_SUBTITLE(
                config.TENANT,
              )
            : tokenizeErrors.length > 0
            ? tokenizeErrors[0].message
            : paymentTextConstants.ADD_PAYMENT_AGAIN
        }
        buttons={
          isCorpTenant(config.TENANT)
            ? [primaryButton]
            : [primaryButton, secondaryButton]
        }
        isMobile={matchesMobile}
      />
    </>
  );
};
